/**
 * Step 3 : Sélection des pièces couvertes
 * Appliqué pour tous les types de garantie (Garantie et Prestation)
 */

import { useState, useMemo, useEffect } from 'react';
import { Package, CheckSquare, Sparkles, Star, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { WarrantyFormState } from '@/features/warranties/hooks/useWarrantyFormWizard';
import { usePartsTree } from '@/features/warranties/hooks/useParts';
import { useWarrantyPermissions } from '@/features/warranties/hooks/useWarrantyPermissions';
import { useCoveragePresets } from '@/features/warranties/hooks/useCoveragePresets';
import { useNotifications } from '@/contexts/NotificationContext';
import { useAuth } from '@/hooks/auth/useAuth';
import { CategorySection } from '@/features/warranties/components/CategorySection';
import { transformPartsTreeToCategories } from '@/features/warranties/utils/transformPartsTree';
import { getWarrantyTypeWording } from '@/utils/vendorWording';
interface Step3Props {
  formData: WarrantyFormState;
  updateFormData: (updates: Partial<WarrantyFormState>) => void;
  onCanProceedChange: (canProceed: boolean) => void;
  showErrors?: boolean;
}

export const Step3PartSelection = ({ formData, updateFormData, onCanProceedChange, showErrors = false }: Step3Props) => {
  const wording = useMemo(() => getWarrantyTypeWording(formData.warrantyType), [formData.warrantyType]);
  const { showNotification } = useNotifications();
  const { user } = useAuth();
  const permissions = useWarrantyPermissions(formData.societyId);
  const { data: partsTree, isLoading } = usePartsTree('fr', formData.vehicleType);
  const { data: coveragePresets } = useCoveragePresets(formData.vehicleType);

  // Déterminer si l'utilisateur est externe
  const isExternalUser = useMemo(() => user?.type === 'EXTERNAL', [user?.type]);

  // État local pour gérer les catégories expandées
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // État pour afficher/masquer la liste complète (true par défaut pour external users)
  const [showFullList, setShowFullList] = useState(isExternalUser);

  // Récupérer les catégories filtrées par vehicleType depuis l'API
  const categories = useMemo(() => {
    return transformPartsTreeToCategories(partsTree);
  }, [partsTree]);

  // Calculer les compteurs
  const counters = useMemo(() => {
    const selectedInSection = formData.selectedPartIds.length;
    const categoriesWithSelection = categories.filter(cat =>
      cat.parts.some(part => formData.selectedPartIds.includes(part.id))
    ).length;

    return {
      selectedCategories: categoriesWithSelection,
      selectedParts: selectedInSection,
    };
  }, [categories, formData.selectedPartIds]);

  // Gérer le toggle d'une catégorie
  const handleCategoryToggle = (categoryKey: string) => {
    const category = categories.find(c => c.catKey === categoryKey);
    if (!category) return;

    const isCurrentlyExpanded = expandedCategories.has(categoryKey);
    const newExpanded = new Set(expandedCategories);

    if (isCurrentlyExpanded) {
      // Fermer et décocher toutes les pièces
      newExpanded.delete(categoryKey);
      const partIdsToRemove = category.parts.map(p => p.id);
      const newSelectedIds = formData.selectedPartIds.filter(id => !partIdsToRemove.includes(id));
      updateFormData({ selectedPartIds: newSelectedIds });
    } else {
      // Ouvrir et cocher les pièces priorité 1
      newExpanded.add(categoryKey);
      const priority1Parts = category.parts.filter(p => p.selectionPriority === 1);
      const priority1Ids = priority1Parts.map(p => p.id);
      const newSelectedIds = [...new Set([...formData.selectedPartIds, ...priority1Ids])];
      updateFormData({ selectedPartIds: newSelectedIds });
    }

    setExpandedCategories(newExpanded);
  };

  // Gérer le toggle d'une pièce
  const handlePartToggle = (partId: number, priority: number) => {
    const isSelected = formData.selectedPartIds.includes(partId);

    if (isSelected) {
      // Décocher - vérifier les permissions pour priorité 1
      if (priority === 1 && !permissions.canModifyDefaultParts) {
        showNotification('warning', "Vous ne pouvez pas décocher les pièces par défaut. Contactez votre commercial.");
        return;
      }
      const newSelectedIds = formData.selectedPartIds.filter(id => id !== partId);
      updateFormData({ selectedPartIds: newSelectedIds });
    } else {
      // Cocher
      const newSelectedIds = [...formData.selectedPartIds, partId];
      updateFormData({ selectedPartIds: newSelectedIds });
    }
  };

  // Gérer le bouton "toutes/aucune" pour une priorité
  const handleSelectAllPriority = (categoryKey: string, priority: number) => {
    const category = categories.find(c => c.catKey === categoryKey);
    if (!category) return;

    const priorityParts = category.parts.filter(p => p.selectionPriority === priority);
    const priorityIds = priorityParts.map(p => p.id);
    const allSelected = priorityIds.every(id => formData.selectedPartIds.includes(id));

    if (allSelected) {
      // Décocher toutes
      if (priority === 1 && !permissions.canModifyDefaultParts) {
        showNotification('warning', "Vous ne pouvez pas décocher les pièces par défaut. Contactez votre commercial.");
        return;
      }
      const newSelectedIds = formData.selectedPartIds.filter(id => !priorityIds.includes(id));
      updateFormData({ selectedPartIds: newSelectedIds });
    } else {
      // Cocher toutes
      const newSelectedIds = [...new Set([...formData.selectedPartIds, ...priorityIds])];
      updateFormData({ selectedPartIds: newSelectedIds });
    }
  };

  // Gérer le bouton "Tout sélectionner" global
  const handleSelectAll = () => {
    const allPartIds = categories.flatMap(cat => cat.parts.map(p => p.id));
    const allSelected = allPartIds.every(id => formData.selectedPartIds.includes(id));

    if (allSelected) {
      // Tout désélectionner
      updateFormData({ selectedPartIds: [] });
      setExpandedCategories(new Set());
    } else {
      // Tout sélectionner
      updateFormData({ selectedPartIds: allPartIds });
      const allCategoryIds = categories.map(cat => cat.catKey);
      setExpandedCategories(new Set(allCategoryIds));
    }
  };

  // Appliquer un preset de couverture
  const handleApplyPreset = (partIds: number[]) => {
    updateFormData({ selectedPartIds: partIds });

    // Expander toutes les catégories qui ont au moins une pièce sélectionnée
    const categoriesToExpand = categories
      .filter(cat => cat.parts.some(part => partIds.includes(part.id)))
      .map(cat => cat.catKey);

    setExpandedCategories(new Set(categoriesToExpand));
  };

  const isValid = formData.selectedPartIds.length > 0;

  // Remonter la validité au container
  useEffect(() => {
    onCanProceedChange(isValid);
  }, [isValid, onCanProceedChange]);

  const allSelected = useMemo(() => {
    const allPartIds = categories.flatMap(cat => cat.parts.map(p => p.id));
    return allPartIds.length > 0 && allPartIds.every(id => formData.selectedPartIds.includes(id));
  }, [categories, formData.selectedPartIds]);

  if (isLoading) {
    return (
      <div className="tw-flex tw-justify-center tw-items-center tw-py-12">
        <div className="tw-animate-spin tw-h-8 tw-w-8 tw-border-4 tw-border-affinicar-primary tw-border-t-transparent tw-rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="tw-max-w-6xl tw-mx-auto tw-p-4">

      <div className="tw-relative tw-p-6 tw-space-y-5">

      {/* Titre de la step */}
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-1">
        <Package className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
        <h4 className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">Pièces couvertes</h4>
      </div>

      {/* Recommandations de couverture - MISE EN AVANT (uniquement INTERNAL, masqué si preset warranty) */}
      {!isExternalUser && (
        formData.appliedWarrantyPresetId ? (
          <div className="tw-p-6">
            <div className="tw-flex tw-items-start tw-gap-3">
              <Sparkles className="tw-w-6 tw-h-6 tw-text-affinicar-primary tw-flex-shrink-0 tw-mt-1" />
              <div>
                <h4 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-1">
                  Preset {wording.deType} appliqué
                </h4>
                <p className="tw-text-sm tw-text-affinicar-secondary">
                  Un preset {wording.deType} est appliqué. Les recommandations de couverture ne sont pas affichées.
                  Vous pouvez modifier manuellement les pièces ci-dessous.
                </p>
              </div>
            </div>
          </div>
        ) : (
          coveragePresets && coveragePresets.length > 0 && (
          <div id="parts-recommendations-container" className="tw-p-6">
            <div className="tw-flex tw-items-center tw-gap-3 tw-mb-5">
              <div className="tw-p-2 tw-bg-affinicar-primary tw-rounded-lg">
                <Sparkles className="tw-w-6 tw-h-6 tw-text-white" />
              </div>
              <div>
                <h3 className="tw-text-lg tw-font-bold tw-text-gray-900">
                  Recommandations de couverture
                </h3>
                <p className="tw-text-sm tw-text-gray-600">
                  Appliquez une recommandation adaptée à votre offre
                </p>
              </div>
            </div>

            <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
              {coveragePresets.map((preset, index) => {
                const isActive =
                  preset.partIds.length === formData.selectedPartIds.length &&
                  preset.partIds.every(id => formData.selectedPartIds.includes(id));

                const icons = [
                  { Icon: Package, color: 'tw-text-green-600', bgColor: 'tw-bg-green-100' },
                  { Icon: Star, color: 'tw-text-affinicar-primary', bgColor: 'tw-bg-affinicar-lighter' },
                  { Icon: CheckCircle, color: 'tw-text-purple-600', bgColor: 'tw-bg-purple-100' },
                ];

                const { Icon, color, bgColor } = icons[index] || icons[0];

                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => handleApplyPreset(preset.partIds)}
                    className={`tw-flex tw-flex-col tw-items-start tw-p-5 tw-rounded-xl tw-border-2 tw-transition-all tw-text-left ${
                      isActive
                        ? 'tw-bg-affinicar-primary tw-border-affinicar-primary tw-text-white tw-shadow-xl tw-scale-105'
                        : 'tw-bg-white tw-border-gray-300 tw-text-gray-900 hover:tw-border-affinicar-light hover:tw-shadow-md hover:tw-scale-102'
                    }`}
                  >
                    <div className={`tw-p-2 tw-rounded-lg tw-mb-3 ${isActive ? 'tw-bg-affinicar-lighter0' : bgColor}`}>
                      <Icon className={`tw-w-6 tw-h-6 ${isActive ? 'tw-text-white' : color}`} />
                    </div>
                    <h4 className="tw-text-base tw-font-bold tw-mb-2">{preset.label}</h4>
                    <p className={`tw-text-sm tw-mb-3 ${isActive ? 'tw-text-affinicar-lighter' : 'tw-text-gray-600'}`}>
                      {preset.description || 'Configuration recommandée'}
                    </p>
                    <div className="tw-flex tw-items-center tw-gap-2 tw-mt-auto">
                      <span className={`tw-px-3 tw-py-1.5 tw-rounded-full tw-text-sm tw-font-bold ${
                        isActive
                          ? 'tw-bg-affinicar-lighter0 tw-text-white'
                          : 'tw-bg-gray-100 tw-text-gray-700'
                      }`}>
                        {preset.partsCount} pièces
                      </span>
                      {isActive && (
                        <CheckCircle className="tw-w-5 tw-h-5 tw-text-white" />
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          )
        )
      )}

      {/* Wrapper pour le tutoriel - englobe compteur + liste */}
      <div id="parts-selection-container" className="tw-space-y-6">
        {/* Compteur visible et bouton toggle */}
        <div className="tw-flex tw-items-center tw-justify-between tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-p-4">
          <div className="tw-flex tw-items-center tw-gap-4">
            <div className="tw-flex tw-items-center tw-gap-2">
              <Package className="tw-w-5 tw-h-5 tw-text-gray-500" />
              <div>
                <p className="tw-text-sm tw-font-medium tw-text-gray-700">
                  <span className="tw-font-bold tw-text-gray-900">{counters.selectedCategories}</span> catégories
                </p>
                <p className="tw-text-xs tw-text-gray-500">sur {categories.length} disponibles</p>
              </div>
            </div>
            <div className="tw-w-px tw-h-10 tw-bg-gray-200" />
            <div>
              <p className="tw-text-sm tw-font-medium tw-text-gray-700">
                <span className="tw-font-bold tw-text-gray-900">{counters.selectedParts}</span> pièces sélectionnées
              </p>
              <p className="tw-text-xs tw-text-gray-500">
                sur {categories.flatMap(c => c.parts).length} disponibles
              </p>
            </div>
          </div>

          <button
            id="parts-toggle-button"
            type="button"
            onClick={() => setShowFullList(!showFullList)}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-gray-50 tw-border tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-100 tw-transition-colors"
          >
            {showFullList ? (
              <>
                <ChevronUp className="tw-w-4 tw-h-4" />
                Masquer la liste
              </>
            ) : (
              <>
                <ChevronDown className="tw-w-4 tw-h-4" />
                Personnaliser ma couverture
              </>
            )}
          </button>
        </div>

        {showErrors && formData.selectedPartIds.length === 0 && (
          <p className="tw-text-sm tw-text-red-600">Au moins une pièce doit être sélectionnée</p>
        )}

        {/* Zone de contenu scrollable avec les catégories - TOGGLE */}
        {showFullList && (
          <div className="tw-flex-1 tw-min-h-0 tw-flex tw-flex-col">
            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-200 tw-flex-1 tw-min-h-0 tw-flex tw-flex-col">
              {/* Header interne avec bouton discret */}
              <div className="tw-flex tw-items-center tw-justify-between tw-px-6 tw-py-4 tw-border-b tw-border-gray-200">
                <h3 className="tw-text-sm tw-font-medium tw-text-gray-700">
                  Liste complète des pièces
                </h3>
                {permissions.canSelectAllParts && (
                  <button
                    type="button"
                    onClick={handleSelectAll}
                    data-testid="btn-select-all-parts"
                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-bg-transparent tw-border tw-border-gray-300 tw-rounded-md hover:tw-bg-gray-50 hover:tw-text-gray-800 hover:tw-border-gray-400 tw-transition-colors"
                  >
                    <CheckSquare className="tw-w-3.5 tw-h-3.5" />
                    {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
                  </button>
                )}
              </div>

              {/* Contenu scrollable */}
              <div className="tw-p-6 tw-flex-1 tw-min-h-0 tw-overflow-y-auto tw-space-y-2">
                {categories.map((category) => {
                  return (
                    <CategorySection
                      key={category.catKey}
                      categoryKey={category.catKey}
                      categoryLabel={category.label}
                      parts={category.parts}
                      vehicleType={formData.vehicleType === 1 ? 'collection' : formData.vehicleType === 2 ? 'two_wheeler' : 'classic'}
                      selectedPartIds={formData.selectedPartIds}
                      isExpanded={expandedCategories.has(category.catKey)}
                      canModifyDefaultParts={permissions.canModifyDefaultParts}
                      onCategoryToggle={() => handleCategoryToggle(category.catKey)}
                      onPartToggle={handlePartToggle}
                      onSelectAllPriority={handleSelectAllPriority}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      </div>
    </div>
  );
};
